import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Monitor, Smartphone, Tablet, Laptop, Globe, Shield, Loader2, LogOut,
  RefreshCw, Ban,
} from "lucide-react";
import { toast } from "sonner";

function DeviceIcon({ label, size = 20 }) {
  const l = (label || "").toLowerCase();
  if (l.includes("ios") || l.includes("iphone")) return <Smartphone size={size} />;
  if (l.includes("android")) return <Smartphone size={size} />;
  if (l.includes("ipad") || l.includes("tablet")) return <Tablet size={size} />;
  if (l.includes("mac") || l.includes("linux")) return <Laptop size={size} />;
  if (l.includes("windows")) return <Monitor size={size} />;
  return <Globe size={size} />;
}

function timeAgo(iso) {
  if (!iso) return "—";
  try {
    const then = new Date(iso).getTime();
    const diff = (Date.now() - then) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} day(s) ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return "—"; }
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function Devices() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type, session}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/sessions");
      setSessions(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doRevoke = async (s) => {
    setRevoking(s.id);
    try {
      await api.post("/auth/sessions/revoke", { session_id: s.id });
      toast.success("Device signed out");
      // Optimistic removal.
      setSessions((prev) => prev.filter((x) => x.id !== s.id));
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally {
      setRevoking(null);
      setConfirm(null);
    }
  };

  const doRevokeAllOthers = async () => {
    setRevokingAll(true);
    try {
      const r = await api.post("/auth/sessions/revoke-all-others");
      const n = r.data?.revoked_count || 0;
      toast.success(
        n === 0 ? "No other devices to sign out" :
        `All other devices signed out (${n})`
      );
      setSessions((prev) => prev.filter((x) => x.is_current));
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    } finally {
      setRevokingAll(false);
      setConfirm(null);
    }
  };

  const others = sessions.filter((s) => !s.is_current);
  const current = sessions.find((s) => s.is_current);

  return (
    <section className="space-y-6" data-testid="devices-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="accp-label flex items-center gap-1">
            <Shield size={11} /> My Account · Security
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold text-primary"
              style={{ fontFamily: "Outfit" }}>
            Devices &amp; Sessions
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            These are the devices currently signed in with <b>Remember me</b>.
            Sign out any device you don't recognise — the attacker will be locked
            out on its next request.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="accp-btn-ghost" data-testid="sessions-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={() => setConfirm({ type: "all" })}
            disabled={others.length === 0 || revokingAll}
            className="accp-btn-ghost disabled:opacity-40"
            data-testid="sessions-revoke-all-others"
            title={others.length === 0 ? "No other devices to sign out" : ""}
          >
            {revokingAll ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
            Sign out all other devices
          </button>
        </div>
      </div>

      {loading ? (
        <div className="accp-card p-8 text-sm text-muted-foreground text-center">
          <Loader2 size={14} className="inline animate-spin mr-2" /> Loading sessions…
        </div>
      ) : sessions.length === 0 ? (
        <div className="accp-card p-8 text-sm text-center text-muted-foreground"
             data-testid="sessions-empty">
          You have no remembered sessions. Devices only appear here when you log in
          with <b>Remember me</b> checked.
        </div>
      ) : (
        <div className="space-y-3">
          {current && <SessionCard session={current} />}
          {others.length > 0 && (
            <>
              <div className="accp-label pt-4">Other devices ({others.length})</div>
              {others.map((s) => (
                <SessionCard
                  key={s.id}
                  session={s}
                  onRevoke={() => setConfirm({ type: "one", session: s })}
                  busy={revoking === s.id}
                />
              ))}
            </>
          )}
          {others.length === 0 && current && (
            <div className="text-xs text-muted-foreground pl-1" data-testid="no-other-devices">
              No other devices are currently signed in. You're all clear.
            </div>
          )}
        </div>
      )}

      {confirm && (
        <ConfirmModal
          type={confirm.type}
          session={confirm.session}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm.type === "all") doRevokeAllOthers();
            else doRevoke(confirm.session);
          }}
          busy={revokingAll || !!revoking}
        />
      )}
    </section>
  );
}

function SessionCard({ session, onRevoke, busy }) {
  const isCurrent = session.is_current;
  return (
    <div
      className={`accp-card p-4 flex items-start gap-4 ${
        isCurrent ? "border-primary bg-primary/5" : ""
      }`}
      data-testid={`session-row-${session.id}`}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0
                       ${isCurrent ? "bg-primary text-white" : "bg-accent/50 text-primary"}`}>
        <DeviceIcon label={session.device_label} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-primary">
            {session.device_label}
          </span>
          {isCurrent && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary text-white"
                  data-testid="current-session-badge">
              Current session
            </span>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          IP: {session.ip_address_masked || "unknown"} ·
          Last active <b>{timeAgo(session.last_used_at)}</b>
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5">
          Signed in {formatDate(session.created_at)} · expires {formatDate(session.expires_at)}
        </div>
      </div>
      {!isCurrent && (
        <button
          onClick={onRevoke}
          disabled={busy}
          className="accp-btn-ghost text-[#B71C1C] hover:bg-[#FAD7D7]"
          data-testid={`revoke-${session.id}`}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}
          Sign out device
        </button>
      )}
    </div>
  );
}

function ConfirmModal({ type, session, onCancel, onConfirm, busy }) {
  const isAll = type === "all";
  return (
    <div className="accp-modal-shell" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="accp-modal-card space-y-4"
           data-testid="sessions-confirm-modal">
        <div>
          <div className="accp-label">Security action</div>
          <h3 className="text-xl font-semibold text-primary" style={{ fontFamily: "Outfit" }}>
            {isAll ? "Sign out all other devices?" : "Sign out this device?"}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground">
          {isAll
            ? "Every other signed-in device will be logged out immediately. You'll stay signed in on this device."
            : <>This will sign out <b>{session?.device_label}</b> last active {timeAgo(session?.last_used_at)}.
              The device will need to log in again to regain access.</>
          }
        </p>
        <div className="flex gap-2 justify-end">
          <button onClick={onCancel} disabled={busy} className="accp-btn-ghost"
                  data-testid="confirm-cancel">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={busy}
                  className="accp-btn-primary bg-[#B71C1C] hover:bg-[#8B1414]"
                  data-testid="confirm-revoke">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
            {isAll ? "Sign out all others" : "Sign out device"}
          </button>
        </div>
      </div>
    </div>
  );
}
